import { api, ApiError } from '../lib/api.js';
import { applyBranding, esc, type Branding } from '../lib/brand.js';
import { shell, centered, loading, type NavItem } from '../lib/shell.js';
import { navigate } from '../lib/router.js';
import { toast } from '../lib/toast.js';
import { icon } from '../lib/icons.js';
import { mountGhlPanel } from '../lib/ghl-panel.js';
import { isPlatformAdmin } from '../lib/auth.js';
import { formBuilderBody, wireFormBuilder, wirePerFormBuilder } from './form-builder.js';

interface Me { authenticated: boolean; user: { email: string; realmRoles: string[] }; memberships: { role: string }[]; actingAgency?: { id: string; name: string } | null }
interface Agency { id: string; name: string; slug: string; branding: Branding; status: string }
interface Business { id: string; name: string; contact_name: string | null; contact_email: string | null; contact_phone: string | null; created_at: string }
interface FormRow { id: string; business_id: string; token: string; title: string; status: string; created_at: string; expires_at: string | null }
interface Submission {
  id: string; form_id: string; employee_name: string; first_name: string | null; last_name: string | null; company_name: string; business_id: string; email: string | null; phone: string | null;
  hire_date: string | null; family_status: Record<string, boolean>; coverage: string[]; status: string; submitted_at: string;
  assigned_to: string | null; assignee_email: string | null;
  extra?: Record<string, unknown>; field_labels?: Record<string, string> | null; spec_version?: number | null;
}

const NAV: NavItem[] = [
  { key: 'overview', href: '/app/overview', label: 'Overview', ico: icon('home') },
  { key: 'businesses', href: '/app/businesses', label: 'Businesses', ico: icon('building') },
  { key: 'forms', href: '/app/forms', label: 'Forms', ico: icon('clipboard') },
  { key: 'form-builder', href: '/app/form-builder', label: 'Form Builder', ico: icon('palette') },
  { key: 'submissions', href: '/app/submissions', label: 'Submissions', ico: icon('inbox') },
  { key: 'settings', href: '/app/settings', label: 'Settings', ico: icon('settings') },
];
const TITLES: Record<string, string> = {
  overview: 'Overview', businesses: 'Businesses', forms: 'Forms', 'form-builder': 'Form Builder', submissions: 'Submissions', settings: 'Settings',
};

/** Shared auth + tenant bootstrap. Redirects to /login or shows an error, returning null then. */
async function bootstrap(root: HTMLElement): Promise<{ me: Me; agency: Agency } | null> {
  root.innerHTML = loading();
  let me: Me;
  try { me = await api.get<Me>('/auth/me'); } catch { me = { authenticated: false } as Me; }
  if (!me.authenticated) { navigate('/login'); return null; }
  let agency: Agency;
  try { agency = (await api.get<{ agency: Agency }>('/api/agency')).agency; }
  catch (err) {
    // A platform admin on a host with no resolved tenant (e.g. the control-plane
    // domain, before onboarding any agency) belongs in the platform console, not
    // this dead-end. Non-admins genuinely have no agency here, so show the notice.
    if (err instanceof ApiError && err.status === 404 && isPlatformAdmin(me.user.realmRoles) && !me.actingAgency) {
      navigate('/platform');
      return null;
    }
    root.innerHTML = centered(err instanceof ApiError && err.status === 404 ? 'No agency is configured for this address.' : 'Could not load your agency.');
    return null;
  }
  applyBranding(agency.branding);
  return { me, agency };
}

function bannerFor(me: Me): string {
  return me.actingAgency
    ? `<div class="acting-banner">${icon('shield')} Apex admin — viewing <strong>${esc(me.actingAgency.name)}</strong> as support
        <button class="btn sm" id="exitImpersonate">Exit to platform</button></div>`
    : '';
}
function wireExit(root: HTMLElement) {
  const exitBtn = root.querySelector('#exitImpersonate') as HTMLButtonElement | null;
  if (exitBtn) exitBtn.onclick = async () => { try { await api.post('/api/platform/stop-impersonate'); } catch { /* */ } navigate('/platform'); };
}

export async function renderDashboard(root: HTMLElement, sub: string): Promise<void> {
  const ctx = await bootstrap(root);
  if (!ctx) return;
  const { me, agency } = ctx;

  // Form Builder edits the agency-wide default form, so it's admin-only.
  const isAdmin = isPlatformAdmin(me.user.realmRoles) || !!me.actingAgency || me.memberships?.[0]?.role === 'agency_admin';
  const allowed = ['overview', 'businesses', 'forms', 'submissions', 'settings', ...(isAdmin ? ['form-builder'] : [])];
  const active = allowed.includes(sub) ? sub : 'overview';
  const nav = isAdmin ? NAV : NAV.filter((n) => n.key !== 'form-builder');
  const role = isPlatformAdmin(me.user.realmRoles) ? 'Platform admin' : (me.memberships?.[0]?.role ?? 'Staff');

  let body = '';
  let headAction = '';
  try {
    if (active === 'overview') body = await overviewBody(agency);
    else if (active === 'businesses') { body = await businessesBody(); headAction = `<button class="btn" id="addBusiness">${icon('plus')} Add business</button>`; }
    else if (active === 'forms') { body = await formsBody(); headAction = `<button class="btn" id="newForm">${icon('plus')} New form</button>`; }
    else if (active === 'form-builder') body = formBuilderBody();
    else if (active === 'submissions') { body = await submissionsBody(); headAction = `<button class="btn secondary" id="exportBtn">${icon('download')} Export CSV</button>`; }
    else body = await settingsBody(agency);
  } catch (err) {
    body = `<div class="note">Failed to load: ${esc(err instanceof ApiError ? err.message : 'error')}</div>`;
  }

  root.innerHTML = shell({ brandName: agency.name, sub: 'Benefits', nav, activeKey: active, title: TITLES[active], headAction, userEmail: me.user.email, userRole: me.actingAgency ? 'Apex admin (acting)' : role, body, banner: bannerFor(me), logoUrl: agency.branding?.logo_url });
  wireExit(root);

  if (active === 'businesses') wireBusinesses(root);
  else if (active === 'forms') wireForms(root);
  else if (active === 'form-builder') wireFormBuilder(root, agency);
  else if (active === 'submissions') wireSubmissions(root);
  else if (active === 'settings') wireSettings(root, agency);
}

/* ============================ Settings ============================ */
interface Staff { user_id: string; email: string; name: string | null; role: string; last_login_at: string | null }
interface Activity { id: string; action: string; target_type: string | null; actor_email: string | null; created_at: string; meta: Record<string, unknown> }

const SETTINGS_TABS = [['branding', 'Branding'], ['team', 'Team'], ['integrations', 'Integrations'], ['activity', 'Activity']];

function settingsTab(): string {
  return new URLSearchParams(location.search).get('tab') || 'branding';
}

async function settingsBody(agency: Agency): Promise<string> {
  const tab = settingsTab();
  const tabBar = `<div class="tabs" style="margin-bottom:20px">${SETTINGS_TABS.map(([k, l]) => `<a href="/app/settings?tab=${k}" data-link class="${k === tab ? 'active' : ''}">${l}</a>`).join('')}</div>`;
  let panel = '';
  try {
    if (tab === 'team') panel = await teamPanel();
    else if (tab === 'integrations') panel = await integrationsPanel();
    else if (tab === 'activity') panel = await activityPanel();
    else panel = brandingBody(agency);
  } catch (e) {
    panel = `<div class="note">Failed to load: ${esc(e instanceof ApiError ? e.message : 'error')}</div>`;
  }
  return tabBar + `<div id="settings-panel">${panel}</div>`;
}

function wireSettings(root: HTMLElement, agency: Agency) {
  const tab = settingsTab();
  if (tab === 'team') wireTeam(root);
  else if (tab === 'integrations') void mountGhlPanel(root);
  else if (tab === 'activity') { /* read-only */ }
  else wireBranding(root, agency);
}

function adminOnly(what: string): string {
  return `<div class="card"><div class="card-body"><div class="empty"><span class="emoji">${icon('lock')}</span>
    <div style="font-weight:600;color:var(--text);font-size:16px">Admin only</div>
    <div>Only agency admins can ${esc(what)}.</div></div></div></div>`;
}

/* ---- Team ---- */
async function teamPanel(): Promise<string> {
  let staff: Staff[];
  try { staff = (await api.get<{ staff: Staff[] }>('/api/agency/staff')).staff; }
  catch (e) { if (e instanceof ApiError && e.status === 403) return adminOnly('manage the team'); throw e; }
  const rows = staff.map((s) => `<tr>
    <td><strong>${esc(s.email)}</strong>${s.name ? `<br/><span class="muted" style="font-size:12.5px">${esc(s.name)}</span>` : ''}</td>
    <td><select class="role-sel" data-id="${s.user_id}"><option value="agency_staff" ${s.role === 'agency_staff' ? 'selected' : ''}>Staff</option><option value="agency_admin" ${s.role === 'agency_admin' ? 'selected' : ''}>Admin</option></select></td>
    <td class="muted">${s.last_login_at ? fmtDateTime(s.last_login_at) : '<span class="badge gray">Invite pending</span>'}</td>
    <td style="white-space:nowrap">
      ${s.last_login_at ? '' : `<button class="btn ghost sm staff-invite" data-id="${s.user_id}" data-email="${esc(s.email)}">Resend invite</button> `}
      <button class="btn ghost sm staff-del" data-id="${s.user_id}" data-email="${esc(s.email)}">Remove</button>
    </td>
  </tr>`).join('');
  return card('Team members', `<table><thead><tr><th>Email</th><th>Role</th><th>Last login</th><th></th></tr></thead><tbody>${rows}</tbody></table>`)
    + `<div class="section-title">Add a team member</div>
       <div class="grid-2"><input type="email" id="staff-email" placeholder="name@agency.com" /><select id="staff-role"><option value="agency_staff">Staff</option><option value="agency_admin">Admin</option></select></div>
       <button class="btn sm" id="staff-add" style="margin-top:10px">Add member</button>
       <p class="muted" style="font-size:12px;margin-top:8px">New members get an email with a link to set their password and sign in. Use "Resend invite" if it doesn't arrive.</p>`;
}

function wireTeam(root: HTMLElement) {
  const reload = () => navigate('/app/settings?tab=team');
  root.querySelectorAll<HTMLSelectElement>('.role-sel').forEach((sel) => {
    sel.onchange = async () => {
      try { await api.patch(`/api/agency/staff/${sel.dataset.id}`, { role: sel.value }); toast('Role updated'); }
      catch (e) { toast(e instanceof ApiError ? e.message : 'Failed', 'err'); }
    };
  });
  root.querySelectorAll<HTMLButtonElement>('.staff-del').forEach((btn) => {
    btn.onclick = async () => {
      if (!confirm(`Remove ${btn.dataset.email} from this agency?`)) return;
      try { await api.del(`/api/agency/staff/${btn.dataset.id}`); toast('Member removed'); reload(); }
      catch (e) { toast(e instanceof ApiError ? e.message : 'Failed', 'err'); }
    };
  });
  root.querySelectorAll<HTMLButtonElement>('.staff-invite').forEach((btn) => {
    btn.onclick = async () => {
      btn.disabled = true;
      try { await api.post(`/api/agency/staff/${btn.dataset.id}/invite`); toast(`Invite re-sent to ${btn.dataset.email}`); }
      catch (e) { toast(e instanceof ApiError ? e.message : 'Failed', 'err'); btn.disabled = false; }
    };
  });
  (root.querySelector('#staff-add') as HTMLButtonElement).onclick = async () => {
    const email = (root.querySelector('#staff-email') as HTMLInputElement).value.trim();
    if (!email) { toast('Enter an email', 'err'); return; }
    const role = (root.querySelector('#staff-role') as HTMLSelectElement).value;
    try {
      const res = await api.post<{ warning?: string | null; invited?: boolean }>('/api/agency/staff', { email, role });
      toast(res?.warning ? `Member added — ${res.warning}` : res?.invited ? `Invite sent to ${email}` : 'Member added');
      reload();
    }
    catch (e) { toast(e instanceof ApiError ? e.message : 'Failed', 'err'); }
  };
}

/* ---- Integrations (webhooks) ---- */
function integrationsPanel(): string {
  return `<p class="muted" style="margin-top:0">Connect your GHL sub-account to sync form submissions straight into your CRM — contact, tags, workflow enrolment, and pipeline opportunities. The mapping picks up your form's fields automatically.</p><div id="ghlPanel"></div>`;
}

/* ---- Activity ---- */
async function activityPanel(): Promise<string> {
  let activity: Activity[];
  try { activity = (await api.get<{ activity: Activity[] }>('/api/agency/activity')).activity; }
  catch (e) { if (e instanceof ApiError && e.status === 403) return adminOnly('view activity'); throw e; }
  if (!activity.length) return card('Recent activity', '<div class="empty" style="padding:34px"><div class="muted">No activity recorded yet.</div></div>');
  const rows = activity.map((a) => `<tr>
    <td><strong>${esc(actionLabel(a.action))}</strong>${a.target_type ? `<br/><span class="muted" style="font-size:12.5px">${esc(a.target_type)}</span>` : ''}</td>
    <td>${esc(a.actor_email || 'system')}</td>
    <td class="muted">${fmtDateTime(a.created_at)}</td>
  </tr>`).join('');
  return card('Recent activity', `<table><thead><tr><th>Event</th><th>By</th><th>When</th></tr></thead><tbody>${rows}</tbody></table>`);
}

function actionLabel(a: string): string {
  return a.replace(/[._]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/* ============================ Overview ============================ */
async function overviewBody(agency: Agency): Promise<string> {
  const [biz, forms, subs] = await Promise.all([
    api.get<{ businesses: Business[] }>('/api/agency/businesses').then((r) => r.businesses),
    api.get<{ forms: FormRow[] }>('/api/forms').then((r) => r.forms),
    api.get<{ submissions: Submission[] }>('/api/submissions').then((r) => r.submissions),
  ]);
  const newCount = subs.filter((s) => s.status === 'new').length;
  const openForms = forms.filter((f) => f.status === 'open').length;
  const recent = subs.slice(0, 5);
  const recentRows = recent.length
    ? recent.map((s) => `<tr>
        <td><strong>${esc(s.employee_name)}</strong></td><td>${esc(s.company_name)}</td>
        <td class="muted">${fmtDateTime(s.submitted_at)}</td>
        <td><span class="badge ${badge(s.status)}">${esc(stageLabel(s.status))}</span></td></tr>`).join('')
    : `<tr><td colspan="4" class="muted" style="text-align:center;padding:28px">No submissions yet.</td></tr>`;

  return `
    <div class="tiles">
      <a class="tile" href="/app/submissions" data-link><div class="n accent">${subs.length}</div><div class="l">Total submissions</div></a>
      <a class="tile" href="/app/submissions" data-link><div class="n">${newCount}</div><div class="l">New leads</div></a>
      <a class="tile" href="/app/businesses" data-link><div class="n">${biz.length}</div><div class="l">Businesses</div></a>
      <a class="tile" href="/app/forms" data-link><div class="n">${openForms}</div><div class="l">Open forms</div></a>
    </div>
    <div class="row wrap" style="gap:10px;margin-bottom:22px">
      <a class="btn" href="/app/businesses" data-link>${icon('plus')} Add business</a>
      <a class="btn secondary" href="/app/forms" data-link>${icon('plus')} New form</a>
      <a class="btn secondary" href="/app/submissions" data-link>${icon('inbox')} View all submissions</a>
    </div>
    ${analyticsSection(subs)}
    <div class="card"><div class="card-head"><h2>Recent submissions</h2><a href="/app/submissions" data-link style="font-size:13px">View all →</a></div>
      <div class="card-body flush"><table><thead><tr><th>Employee</th><th>Company</th><th>Submitted</th><th>Status</th></tr></thead>
      <tbody>${recentRows}</tbody></table></div></div>`;
}

/** Overview analytics: 30-day submission trend + top coverage interest (client-side aggregation). */
function analyticsSection(subs: Submission[]): string {
  if (!subs.length) return '';
  const days = 30;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const buckets: { key: string; label: string; value: number }[] = [];
  const map: Record<string, number> = {};
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today); d.setDate(d.getDate() - i);
    const key = d.toDateString();
    map[key] = 0;
    buckets.push({ key, label: d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }), value: 0 });
  }
  subs.forEach((s) => { const d = new Date(s.submitted_at); d.setHours(0, 0, 0, 0); const k = d.toDateString(); if (k in map) map[k]++; });
  buckets.forEach((b) => (b.value = map[b.key]));
  const maxDay = Math.max(1, ...buckets.map((b) => b.value));
  const bars = buckets.map((b, i) => `<div class="cbar" title="${b.label}: ${b.value}"><div class="cbar-fill" style="height:${Math.round((b.value / maxDay) * 100)}%"></div>${i % 6 === 0 ? `<div class="cbar-lbl">${b.label}</div>` : ''}</div>`).join('');

  const covCount: Record<string, number> = {};
  subs.forEach((s) => (s.coverage || []).forEach((c) => (covCount[c] = (covCount[c] || 0) + 1)));
  const cov = Object.entries(covCount).sort((a, b) => b[1] - a[1]).slice(0, 6);
  const covMax = Math.max(1, ...cov.map(([, v]) => v));
  const covBars = cov.length
    ? cov.map(([k, v]) => `<div class="hbar"><div class="hbar-lbl">${esc(pretty(k))}</div><div class="hbar-track"><div class="hbar-fill" style="width:${Math.round((v / covMax) * 100)}%"></div></div><div class="hbar-val">${v}</div></div>`).join('')
    : '<div class="muted" style="font-size:13px">No coverage data.</div>';

  return `<div class="grid-2" style="gap:16px;margin-bottom:24px;align-items:start">
    <div class="card"><div class="card-head"><h2>Submissions · last 30 days</h2></div><div class="card-body"><div class="chart-bars">${bars}</div></div></div>
    <div class="card"><div class="card-head"><h2>Top coverage interest</h2></div><div class="card-body">${covBars}</div></div>
  </div>`;
}
function pretty(k: string): string { return k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()); }

/* ============================ Businesses ============================ */
async function businessesBody(): Promise<string> {
  const [businesses, forms, subs] = await Promise.all([
    api.get<{ businesses: Business[] }>('/api/agency/businesses').then((r) => r.businesses),
    api.get<{ forms: FormRow[] }>('/api/forms').then((r) => r.forms),
    api.get<{ submissions: Submission[] }>('/api/submissions').then((r) => r.submissions),
  ]);
  if (!businesses.length) return emptyState('building', 'No businesses yet', 'Add the first employer you’re onboarding.', 'addBusiness', 'Add business');
  const formCount = countBy(forms, (f) => f.business_id);
  const subCount = countBy(subs, (s) => s.business_id);
  const rows = businesses.map((x) => `<tr class="clickable" data-business="${x.id}">
    <td><strong>${esc(x.name)}</strong></td>
    <td>${esc(x.contact_name || '—')}</td>
    <td>${formCount[x.id] || 0}</td>
    <td>${subCount[x.id] || 0}</td>
    <td class="muted">${fmtDate(x.created_at)}</td>
  </tr>`).join('');
  return card('Businesses you onboard',
    `<table><thead><tr><th>Business</th><th>Contact</th><th>Forms</th><th>Submissions</th><th>Added</th></tr></thead><tbody>${rows}</tbody></table>`);
}

function wireBusinesses(root: HTMLElement) {
  // Both the topbar action and the empty-state CTA share #addBusiness.
  root.querySelectorAll<HTMLButtonElement>('#addBusiness').forEach((btn) => (btn.onclick = () => addBusinessDrawer(root)));
  root.querySelectorAll<HTMLElement>('tr[data-business]').forEach((tr) => {
    tr.onclick = () => navigate(`/app/businesses/${tr.dataset.business}`);
  });
}

function addBusinessDrawer(root: HTMLElement) {
  drawer(root, 'Add a business', `
    <div class="field"><label>Business name *</label><input type="text" id="d_name" placeholder="e.g. Riverside Logistics" /></div>
    <div class="field"><label>Primary contact name</label><input type="text" id="d_contact" /></div>
    <div class="grid-2">
      <div class="field"><label>Contact email</label><input type="email" id="d_email" /></div>
      <div class="field"><label>Contact phone</label><input type="tel" id="d_phone" /></div>
    </div>
    <button class="btn block" data-submit>Add business</button>`,
    async (scrim) => {
      const name = inp(scrim, 'd_name'); if (!name) return 'Business name is required';
      await api.post('/api/agency/businesses', { name, contact_name: inp(scrim, 'd_contact'), contact_email: inp(scrim, 'd_email'), contact_phone: inp(scrim, 'd_phone') });
      toast('Business added');
      navigate('/app/businesses');
    });
}

/* ============================ Forms ============================ */
async function formsBody(): Promise<string> {
  const [forms, businesses, subs] = await Promise.all([
    api.get<{ forms: FormRow[] }>('/api/forms').then((r) => r.forms),
    api.get<{ businesses: Business[] }>('/api/agency/businesses').then((r) => r.businesses),
    api.get<{ submissions: Submission[] }>('/api/submissions').then((r) => r.submissions),
  ]);
  if (!forms.length) return emptyState('clipboard', 'No forms yet', 'Create a benefits form for one of your businesses.', 'newForm', 'New form');
  const bizName: Record<string, string> = {};
  businesses.forEach((b) => (bizName[b.id] = b.name));
  const perForm = countBy(subs, (s) => s.form_id || '');
  return card('Provisioned forms', formsTableHtml(forms, bizName, perForm));
}

/** Shared forms table (used by the Forms page and the business detail page). */
function formsTableHtml(forms: FormRow[], bizName: Record<string, string>, perForm: Record<string, number>): string {
  const rows = forms.map((f) => {
    const st = f.status === 'open' ? 'green' : f.status === 'draft' ? 'gray' : 'amber';
    const link = `${location.origin}/f/${f.token}`;
    return `<tr>
      <td><strong>${esc(f.title)}</strong><br/><span class="muted" style="font-size:12.5px">${esc(bizName[f.business_id] || '')} · /f/${esc(f.token)}</span></td>
      <td><span class="badge ${st}">${esc(f.status)}</span></td>
      <td>${perForm[f.id] || 0}</td>
      <td style="white-space:nowrap">
        <a class="btn ghost sm" href="/f/${esc(f.token)}" target="_blank" rel="noopener">${icon('external')} Preview</a>
        <button class="btn ghost sm copybtn" data-link="${esc(link)}">${icon('copy')} Copy link</button>
        ${f.status === 'open'
          ? `<button class="btn ghost sm statusbtn" data-id="${f.id}" data-status="closed">Close</button>`
          : `<button class="btn ghost sm statusbtn" data-id="${f.id}" data-status="open">Open</button>`}
        <button class="btn ghost sm editform" data-id="${f.id}" data-title="${esc(f.title)}" data-token="${esc(f.token)}" data-expires="${f.expires_at ? String(f.expires_at).slice(0, 10) : ''}">Edit</button>
        <a class="btn ghost sm" href="/app/forms/${f.id}/customize" data-link>Customize fields</a>
        <button class="btn ghost sm blankpdf" data-id="${f.id}" data-title="${esc(f.title)}">${icon('download')} Blank PDF</button>
        <button class="btn ghost sm delform" data-id="${f.id}">Delete</button>
      </td>
    </tr>`;
  }).join('');
  return `<table><thead><tr><th>Form</th><th>Status</th><th>Submissions</th><th></th></tr></thead><tbody>${rows}</tbody></table>`;
}

function wireForms(root: HTMLElement) {
  root.querySelectorAll<HTMLButtonElement>('#newForm').forEach((btn) => (btn.onclick = () => newFormDrawer(root)));
  wireFormActions(root, () => navigate('/app/forms'));
}

/** Wires copy/status/edit/delete on any rendered forms table. `reload` re-renders the host page. */
function wireFormActions(root: HTMLElement, reload: () => void) {
  root.querySelectorAll<HTMLButtonElement>('.copybtn').forEach((btn) => {
    btn.onclick = () => { navigator.clipboard?.writeText(btn.dataset.link!).catch(() => {}); toast('Link copied'); };
  });
  // Blank form as a PDF: for filing, or for handing to someone who is going to
  // complete it on paper. Deliberately not a `reload()` action — nothing changed.
  root.querySelectorAll<HTMLButtonElement>('.blankpdf').forEach((btn) => {
    btn.onclick = async () => {
      btn.disabled = true;
      const label = btn.innerHTML;
      btn.textContent = 'Preparing…';
      try {
        await api.download(`/api/forms/${btn.dataset.id}/export.pdf`, `${btn.dataset.title || 'form'}-blank.pdf`);
        toast('Blank form downloaded');
      } catch (e) {
        toast(e instanceof ApiError ? e.message : 'Could not build the PDF', 'err');
      } finally {
        btn.disabled = false;
        btn.innerHTML = label;
      }
    };
  });
  root.querySelectorAll<HTMLButtonElement>('.statusbtn').forEach((btn) => {
    btn.onclick = async () => {
      try { await api.post(`/api/forms/${btn.dataset.id}/status`, { status: btn.dataset.status }); toast(`Form ${btn.dataset.status === 'open' ? 'opened' : 'closed'}`); reload(); }
      catch (e) { toast(e instanceof ApiError ? e.message : 'Failed', 'err'); }
    };
  });
  root.querySelectorAll<HTMLButtonElement>('.editform').forEach((btn) => {
    btn.onclick = () => drawer(root, 'Edit form', `
      <div class="field"><label>Form title *</label><input type="text" id="ef_title" value="${esc(btn.dataset.title || '')}" /></div>
      <div class="field"><label>Public link slug</label><input type="text" id="ef_token" value="${esc(btn.dataset.token || '')}" /><span class="hint">Changing this changes the public URL (/f/slug) and breaks existing links.</span></div>
      <div class="field"><label>Close on (optional)</label><input type="date" id="ef_expires" value="${esc(btn.dataset.expires || '')}" /><span class="hint">After this date the form stops accepting responses.</span></div>
      <button class="btn block" data-submit>Save changes</button>`,
      async (scrim) => {
        const title = inp(scrim, 'ef_title'); if (!title) return 'Title is required';
        const expires = inp(scrim, 'ef_expires');
        await api.patch(`/api/forms/${btn.dataset.id}`, { title, token: inp(scrim, 'ef_token') || undefined, expires_at: expires || null });
        toast('Form updated'); reload();
      });
  });
  root.querySelectorAll<HTMLButtonElement>('.delform').forEach((btn) => {
    btn.onclick = async () => {
      if (!confirm('Delete this form? This permanently deletes the form and all of its submissions.')) return;
      try { await api.del(`/api/forms/${btn.dataset.id}`); toast('Form deleted'); reload(); }
      catch (e) { toast(e instanceof ApiError ? e.message : 'Delete failed', 'err'); }
    };
  });
}

async function newFormDrawer(root: HTMLElement, presetBusinessId?: string) {
  let businesses: Business[] = [];
  try { businesses = (await api.get<{ businesses: Business[] }>('/api/agency/businesses')).businesses; } catch { /* */ }
  const opts = businesses.map((b) => `<option value="${b.id}" ${b.id === presetBusinessId ? 'selected' : ''}>${esc(b.name)}</option>`).join('');
  drawer(root, 'New benefits form', `
    <div class="field"><label>Business *</label>
      ${businesses.length ? `<select id="d_business">${opts}</select>` : `<div class="note">Add a business first.</div>`}</div>
    <div class="field"><label>Form title *</label><input type="text" id="d_title" placeholder="e.g. Q2 Open Enrollment" /></div>
    <div class="field"><label>Public link slug (optional)</label><input type="text" id="d_token" placeholder="auto-generated if blank" /></div>
    <div class="field"><label>Close on (optional)</label><input type="date" id="d_expires" /><span class="hint">After this date the form stops accepting responses. You can change it later.</span></div>
    <button class="btn block" data-submit ${businesses.length ? '' : 'disabled'}>Create &amp; open form</button>`,
    async (scrim) => {
      const business_id = (scrim.querySelector('#d_business') as HTMLSelectElement)?.value;
      const title = inp(scrim, 'd_title');
      if (!business_id) return 'Select a business';
      if (!title) return 'Title is required';
      const r = await api.post<{ form: FormRow }>('/api/forms', { business_id, title, token: inp(scrim, 'd_token') || undefined, expires_at: inp(scrim, 'd_expires') || null });
      await api.post(`/api/forms/${r.form.id}/status`, { status: 'open' }).catch(() => {});
      toast('Form created & opened');
      navigate(presetBusinessId ? `/app/businesses/${presetBusinessId}` : '/app/forms');
    });
}

/* ====================== Per-form field customization ====================== */
export async function renderFormCustomize(root: HTMLElement, formId: string): Promise<void> {
  const ctx = await bootstrap(root);
  if (!ctx) return;
  const { me, agency } = ctx;

  let form: FormRow | undefined;
  try { form = (await api.get<{ forms: FormRow[] }>('/api/forms')).forms.find((f) => f.id === formId); }
  catch { /* fall through to not-found */ }
  if (!form) { toast('Form not found', 'err'); navigate('/app/forms'); return; }

  const isAdmin = isPlatformAdmin(me.user.realmRoles) || !!me.actingAgency || me.memberships?.[0]?.role === 'agency_admin';
  const nav = isAdmin ? NAV : NAV.filter((n) => n.key !== 'form-builder');
  const body = `
    <a href="/app/forms" data-link class="btn ghost sm" style="margin-bottom:14px">← Back to forms</a>
    <div class="note" style="margin-bottom:16px">Customizing fields for <strong>${esc(form.title)}</strong>. These changes apply only to this form and layer on top of your agency default.</div>
    ${formBuilderBody()}`;
  root.innerHTML = shell({ brandName: agency.name, sub: 'Benefits', nav, activeKey: 'forms', title: 'Customize form', userEmail: me.user.email, userRole: me.actingAgency ? 'Apex admin (acting)' : '', body, banner: bannerFor(me), logoUrl: agency.branding?.logo_url });
  wireExit(root);
  wirePerFormBuilder(root, agency, formId);
}

/* ============================ Business detail ============================ */
export async function renderBusinessDetail(root: HTMLElement, id: string): Promise<void> {
  const ctx = await bootstrap(root);
  if (!ctx) return;
  const { me, agency } = ctx;

  let business: Business;
  try { business = (await api.get<{ business: Business }>(`/api/agency/businesses/${id}`)).business; }
  catch { toast('Business not found', 'err'); navigate('/app/businesses'); return; }

  const [forms, subs] = await Promise.all([
    api.get<{ forms: FormRow[] }>('/api/forms').then((r) => r.forms.filter((f) => f.business_id === id)),
    api.get<{ submissions: Submission[] }>(`/api/submissions?businessId=${id}`).then((r) => r.submissions),
  ]);
  const perForm = countBy(subs, (s) => s.form_id || '');
  const formsSection = forms.length
    ? card('', formsTableHtml(forms, { [business.id]: business.name }, perForm), true)
    : `<div class="card"><div class="card-body"><div class="empty"><span class="emoji">${icon('clipboard')}</span><div>No forms for this business yet.</div></div></div></div>`;
  const recent = subs.slice(0, 5);
  const subRows = recent.length
    ? recent.map((s) => `<tr class="clickable" data-id="${s.id}"><td><strong>${esc(s.employee_name)}</strong></td><td>${s.coverage.length} selected</td><td class="muted">${fmtDateTime(s.submitted_at)}</td><td><span class="badge ${badge(s.status)}">${esc(stageLabel(s.status))}</span></td></tr>`).join('')
    : `<tr><td colspan="4" class="muted" style="text-align:center;padding:24px">No submissions yet.</td></tr>`;

  const body = `
    <a href="/app/businesses" data-link class="btn ghost sm" style="margin-bottom:16px">← All businesses</a>
    <div class="card" style="margin-bottom:22px"><div class="card-head"><h2>${esc(business.name)}</h2>
      <div class="row" style="gap:8px"><button class="btn secondary sm" id="editBiz">Edit</button><button class="btn ghost sm" id="delBiz">Delete</button></div></div>
      <div class="card-body"><dl class="kv">
        <dt>Contact</dt><dd>${esc(business.contact_name || '—')}</dd>
        <dt>Email</dt><dd>${esc(business.contact_email || '—')}</dd>
        <dt>Phone</dt><dd>${esc(business.contact_phone || '—')}</dd>
        <dt>Added</dt><dd>${fmtDate(business.created_at)}</dd>
      </dl></div></div>
    ${analyticsSection(subs)}
    <div class="spread" style="margin-bottom:10px"><h3 style="margin:0;font-size:15px">Forms</h3><button class="btn sm" id="newForm">${icon('plus')} New form</button></div>
    ${formsSection}
    <div class="spread" style="margin:24px 0 10px"><h3 style="margin:0;font-size:15px">Recent submissions (${subs.length})</h3><a class="btn secondary sm" href="/app/submissions?business=${id}" data-link>View all</a></div>
    <div class="card"><div class="card-body flush"><table><thead><tr><th>Employee</th><th>Coverage</th><th>Submitted</th><th>Status</th></tr></thead><tbody>${subRows}</tbody></table></div></div>`;

  root.innerHTML = shell({ brandName: agency.name, sub: 'Benefits', nav: NAV, activeKey: 'businesses', title: business.name, userEmail: me.user.email, userRole: me.actingAgency ? 'Apex admin (acting)' : '', body, banner: bannerFor(me), logoUrl: agency.branding?.logo_url });
  wireExit(root);
  (root.querySelector('#newForm') as HTMLButtonElement).onclick = () => newFormDrawer(root, id);
  wireFormActions(root, () => navigate(`/app/businesses/${id}`));
  root.querySelectorAll<HTMLElement>('tr.clickable[data-id]').forEach((tr) => {
    tr.onclick = async () => {
      try { const { submission } = await api.get<{ submission: Submission }>(`/api/submissions/${tr.dataset.id}`); openSubmission(root, submission); }
      catch (e) { toast(e instanceof ApiError ? e.message : 'Failed', 'err'); }
    };
  });
  (root.querySelector('#editBiz') as HTMLButtonElement).onclick = () => drawer(root, 'Edit business', `
    <div class="field"><label>Business name *</label><input type="text" id="eb_name" value="${esc(business.name)}" /></div>
    <div class="field"><label>Primary contact name</label><input type="text" id="eb_contact" value="${esc(business.contact_name || '')}" /></div>
    <div class="grid-2">
      <div class="field"><label>Contact email</label><input type="email" id="eb_email" value="${esc(business.contact_email || '')}" /></div>
      <div class="field"><label>Contact phone</label><input type="tel" id="eb_phone" value="${esc(business.contact_phone || '')}" /></div>
    </div>
    <button class="btn block" data-submit>Save changes</button>`,
    async (scrim) => {
      const name = inp(scrim, 'eb_name'); if (!name) return 'Business name is required';
      await api.patch(`/api/agency/businesses/${id}`, { name, contact_name: inp(scrim, 'eb_contact'), contact_email: inp(scrim, 'eb_email'), contact_phone: inp(scrim, 'eb_phone') });
      toast('Business updated'); navigate(`/app/businesses/${id}`);
    });
  (root.querySelector('#delBiz') as HTMLButtonElement).onclick = async () => {
    if (!confirm(`Delete ${business.name}? This permanently deletes the business, its forms, and all of its submissions.`)) return;
    try { await api.del(`/api/agency/businesses/${id}`); toast('Business deleted'); navigate('/app/businesses'); }
    catch (e) { toast(e instanceof ApiError ? e.message : 'Delete failed', 'err'); }
  };
}

/* ============================ Submissions ============================ */
let subState: { all: Submission[]; businesses: Business[]; q: string; status: string; businessId: string; assignee: string; myEmail: string; sort: string; page: number };
const SUB_PAGE_SIZE = 25;

async function submissionsBody(): Promise<string> {
  const [submissions, businesses, members, me] = await Promise.all([
    api.get<{ submissions: Submission[] }>('/api/submissions').then((r) => r.submissions),
    api.get<{ businesses: Business[] }>('/api/agency/businesses').then((r) => r.businesses),
    api.get<{ members: { user_id: string; email: string }[] }>('/api/agency/members').then((r) => r.members).catch(() => []),
    api.get<{ user: { email: string } }>('/auth/me').then((r) => r.user).catch(() => ({ email: '' })),
  ]);
  const businessId = new URLSearchParams(location.search).get('business') || '';
  subState = { all: submissions, businesses, q: '', status: 'all', businessId, assignee: '', myEmail: me.email, sort: 'new', page: 1 };

  const bizOpts = ['<option value="">All businesses</option>', ...businesses.map((b) => `<option value="${b.id}" ${b.id === businessId ? 'selected' : ''}>${esc(b.name)}</option>`)].join('');
  const assigneeOpts = ['<option value="">All assignees</option>', '<option value="__me__">Assigned to me</option>', '<option value="__unassigned__">Unassigned</option>',
    ...members.map((m) => `<option value="${esc(m.email)}">${esc(m.email)}</option>`)].join('');
  return `
    <div class="filterbar">
      <div class="tabs" id="statusTabs">
        ${['all', 'new', 'assigned', 'signed_up', 'declined', 'left_company'].map((s) => `<button data-status="${s}" class="${s === 'all' ? 'active' : ''}">${stageLabel(s)}</button>`).join('')}
      </div>
      <span class="grow"></span>
      <input type="search" id="subSearch" placeholder="Search name, company, email…" />
      <select id="subBiz">${bizOpts}</select>
      <select id="subAssignee">${assigneeOpts}</select>
      <select id="subSort"><option value="new">Newest</option><option value="old">Oldest</option></select>
    </div>
    <div id="subTable"></div>`;
}

function filteredSubs(): Submission[] {
  let list = subState.all.slice();
  if (subState.businessId) list = list.filter((s) => s.business_id === subState.businessId);
  if (subState.status !== 'all') list = list.filter((s) => s.status === subState.status);
  if (subState.assignee === '__me__') list = list.filter((s) => !!s.assignee_email && s.assignee_email === subState.myEmail);
  else if (subState.assignee === '__unassigned__') list = list.filter((s) => !s.assigned_to);
  else if (subState.assignee) list = list.filter((s) => s.assignee_email === subState.assignee);
  if (subState.q) {
    const q = subState.q.toLowerCase();
    list = list.filter((s) => `${s.employee_name} ${s.company_name} ${s.email ?? ''}`.toLowerCase().includes(q));
  }
  list.sort((a, b) => subState.sort === 'old'
    ? a.submitted_at.localeCompare(b.submitted_at)
    : b.submitted_at.localeCompare(a.submitted_at));
  return list;
}

function renderSubTable(root: HTMLElement) {
  if (!subState || !root.querySelector('#subTable')) return; // drawer may be opened from a non-submissions page
  const list = filteredSubs();
  const counts: Record<string, number> = { all: subState.all.length };
  subState.all.forEach((s) => (counts[s.status] = (counts[s.status] || 0) + 1));
  root.querySelectorAll<HTMLButtonElement>('#statusTabs button').forEach((b) => {
    const k = b.dataset.status!;
    b.classList.toggle('active', k === subState.status);
    b.innerHTML = `${stageLabel(k)} <span class="count">${counts[k] ?? 0}</span>`;
  });
  const host = root.querySelector('#subTable') as HTMLElement;
  if (!list.length) { host.innerHTML = `<div class="empty"><span class="emoji">${icon('inbox')}</span><div>No submissions match this view.</div></div>`; return; }

  const totalPages = Math.max(1, Math.ceil(list.length / SUB_PAGE_SIZE));
  if (subState.page > totalPages) subState.page = totalPages;
  const start = (subState.page - 1) * SUB_PAGE_SIZE;
  const pageItems = list.slice(start, start + SUB_PAGE_SIZE);

  const rows = pageItems.map((s) => `<tr class="clickable" data-id="${s.id}">
    <td><strong>${esc(s.employee_name)}</strong></td>
    <td>${esc(s.company_name)}</td>
    <td>${s.coverage.length} selected</td>
    <td class="muted">${s.assignee_email ? esc(s.assignee_email) : '—'}</td>
    <td class="muted">${fmtDateTime(s.submitted_at)}</td>
    <td><span class="badge ${badge(s.status)}">${esc(stageLabel(s.status))}</span></td>
  </tr>`).join('');
  const pager = list.length > SUB_PAGE_SIZE
    ? `<div class="pager"><span class="muted">Showing ${start + 1}–${start + pageItems.length} of ${list.length}</span>
        <span class="grow"></span>
        <button class="btn secondary sm" data-pg="prev" ${subState.page === 1 ? 'disabled' : ''}>Prev</button>
        <span class="muted" style="font-size:13px">Page ${subState.page} of ${totalPages}</span>
        <button class="btn secondary sm" data-pg="next" ${subState.page === totalPages ? 'disabled' : ''}>Next</button></div>`
    : '';
  host.innerHTML = card('', `<table><thead><tr><th>Employee</th><th>Company</th><th>Coverage</th><th>Assigned</th><th>Submitted</th><th>Status</th></tr></thead><tbody>${rows}</tbody></table>`, true) + pager;

  host.querySelectorAll<HTMLElement>('tr[data-id]').forEach((tr) => {
    tr.onclick = async () => {
      try { const { submission } = await api.get<{ submission: Submission }>(`/api/submissions/${tr.dataset.id}`); openSubmission(root, submission); }
      catch (e) { toast(e instanceof ApiError ? e.message : 'Failed', 'err'); }
    };
  });
  host.querySelectorAll<HTMLButtonElement>('[data-pg]').forEach((b) => {
    b.onclick = () => { subState.page += b.dataset.pg === 'next' ? 1 : -1; renderSubTable(root); };
  });
}

function wireSubmissions(root: HTMLElement) {
  renderSubTable(root);
  (root.querySelector('#subSearch') as HTMLInputElement).oninput = (e) => { subState.q = (e.target as HTMLInputElement).value.trim(); subState.page = 1; renderSubTable(root); };
  (root.querySelector('#subBiz') as HTMLSelectElement).onchange = (e) => { subState.businessId = (e.target as HTMLSelectElement).value; subState.page = 1; renderSubTable(root); };
  (root.querySelector('#subAssignee') as HTMLSelectElement).onchange = (e) => { subState.assignee = (e.target as HTMLSelectElement).value; subState.page = 1; renderSubTable(root); };
  (root.querySelector('#subSort') as HTMLSelectElement).onchange = (e) => { subState.sort = (e.target as HTMLSelectElement).value; subState.page = 1; renderSubTable(root); };
  root.querySelectorAll<HTMLButtonElement>('#statusTabs button').forEach((b) => {
    b.onclick = () => { subState.status = b.dataset.status!; subState.page = 1; renderSubTable(root); };
  });
  const exp = root.querySelector('#exportBtn') as HTMLButtonElement | null;
  if (exp) exp.onclick = () => openExportDrawer(root);
}

/* ---- Custom-field + label helpers (resolve via the per-submission snapshot) ---- */
const SYSTEM_EXTRA_KEYS = new Set(['consent', 'consent_at']);
/** Friendly label for a group option, from the submission's label snapshot. */
function optLabel(s: Submission, group: string, key: string): string {
  return s.field_labels?.[`${group}:${key}`] || key;
}
/** Builder-defined field answers from `extra` (excluding consent), label-resolved. */
function customFields(s: Submission): { label: string; value: string }[] {
  const labels = s.field_labels || {};
  const out: { label: string; value: string }[] = [];
  for (const [k, v] of Object.entries(s.extra || {})) {
    if (SYSTEM_EXTRA_KEYS.has(k)) continue;
    const value = Array.isArray(v)
      ? v.map((o) => labels[`${k}:${o}`] || String(o)).join('; ')
      : (v == null ? '' : String(v));
    out.push({ label: labels[k] || k, value });
  }
  return out;
}

/* ---- CSV export (client-side, respects current filters) ---- */
const CSV_COLS: { key: string; label: string; get: (s: Submission) => string }[] = [
  { key: 'first_name', label: 'First name', get: (s) => s.first_name || '' },
  { key: 'last_name', label: 'Last name', get: (s) => s.last_name || '' },
  { key: 'company', label: 'Company', get: (s) => s.company_name },
  { key: 'email', label: 'Email', get: (s) => s.email || '' },
  { key: 'phone', label: 'Phone', get: (s) => s.phone || '' },
  { key: 'hire_date', label: 'Hire date', get: (s) => s.hire_date || '' },
  { key: 'family', label: 'Family status', get: (s) => Object.entries(s.family_status || {}).filter(([, v]) => v).map(([k]) => optLabel(s, 'family_status', k)).join('; ') },
  { key: 'coverage', label: 'Coverage', get: (s) => (s.coverage || []).map((k) => optLabel(s, 'coverage', k)).join('; ') },
  { key: 'custom', label: 'Custom fields', get: (s) => customFields(s).map((c) => `${c.label}: ${c.value}`).join(' | ') },
  { key: 'status', label: 'Status', get: (s) => s.status },
  { key: 'assigned', label: 'Assigned to', get: (s) => s.assignee_email || '' },
  { key: 'submitted', label: 'Submitted', get: (s) => s.submitted_at },
];

/** Server cap on one batch PDF; mirrors maxBatchPDF in api/export_pdf.go. */
const MAX_PDF_BATCH = 200;

function openExportDrawer(root: HTMLElement) {
  const count = filteredSubs().length;
  const scrim = drawer(root, 'Export submissions', `
    <p class="muted" style="font-size:13px;margin-top:0">Exports the <strong>${count}</strong> submission(s) currently shown (respects your filters).</p>
    <div class="section-title">Format</div>
    <label class="check" style="padding:7px 10px;margin-bottom:6px"><input type="radio" name="expFmt" value="csv" checked />
      <span style="font-size:13px"><strong>Spreadsheet (CSV)</strong> — one row per submission, for sorting and analysis</span></label>
    <label class="check" style="padding:7px 10px;margin-bottom:6px"><input type="radio" name="expFmt" value="pdf" />
      <span style="font-size:13px"><strong>Completed forms (PDF)</strong> — one page per submission, laid out as the form they filled in</span></label>
    <div id="csvCols">
      <div class="section-title">Columns</div>
      ${CSV_COLS.map((c) => `<label class="check" style="padding:7px 10px;margin-bottom:6px"><input type="checkbox" class="csv-col" value="${c.key}" checked /><span style="font-size:13px">${c.label}</span></label>`).join('')}
    </div>
    <div id="pdfNote" class="note" style="display:none;font-size:13px">Each submission is rendered as its own page, with the questions, the answers, and the consent statement the employee accepted.</div>
    <button class="btn block" data-submit>Download</button>`,
    async (scrim) => {
      const fmt = (scrim.querySelector('input[name=expFmt]:checked') as HTMLInputElement).value;
      const rows = filteredSubs();
      if (!rows.length) return 'Nothing to export with the current filters';

      if (fmt === 'pdf') {
        // The cap is enforced server-side; checking here turns a 413 into
        // guidance the agent can act on before waiting for the request.
        if (rows.length > MAX_PDF_BATCH) {
          return `Too many submissions for one PDF (${rows.length}, limit ${MAX_PDF_BATCH}). Narrow your filters first.`;
        }
        await api.downloadPost('/api/submissions/export.pdf', { ids: rows.map((s) => s.id) }, 'submissions.pdf');
        toast(`${rows.length} form(s) downloaded`);
        return;
      }

      const keys = Array.from(scrim.querySelectorAll<HTMLInputElement>('.csv-col:checked')).map((i) => i.value);
      if (!keys.length) return 'Select at least one column';
      const cols = CSV_COLS.filter((c) => keys.includes(c.key));
      const csv = [cols.map((c) => csvCell(c.label)).join(','), ...rows.map((s) => cols.map((c) => csvCell(c.get(s))).join(','))].join('\r\n');
      downloadCsv(csv, `submissions-${new Date().toISOString().slice(0, 10)}.csv`);
      toast('CSV downloaded');
    });

  // The column picker only applies to CSV; leaving it visible under the PDF
  // option would imply the columns do something there.
  const cols = scrim.querySelector('#csvCols') as HTMLElement;
  const note = scrim.querySelector('#pdfNote') as HTMLElement;
  scrim.querySelectorAll<HTMLInputElement>('input[name=expFmt]').forEach((radio) => {
    radio.onchange = () => {
      const isPdf = (scrim.querySelector('input[name=expFmt]:checked') as HTMLInputElement).value === 'pdf';
      cols.style.display = isPdf ? 'none' : '';
      note.style.display = isPdf ? '' : 'none';
    };
  });
}

function csvCell(v: unknown): string {
  const s = String(v ?? '');
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function downloadCsv(content: string, filename: string) {
  const blob = new Blob(['﻿' + content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

interface Member { user_id: string; email: string }
interface Note { id: string; author_email: string | null; body: string; created_at: string }

async function openSubmission(root: HTMLElement, s: Submission) {
  const [members, notes] = await Promise.all([
    api.get<{ members: Member[] }>('/api/agency/members').then((r) => r.members).catch(() => [] as Member[]),
    api.get<{ notes: Note[] }>(`/api/submissions/${s.id}/notes`).then((r) => r.notes).catch(() => [] as Note[]),
  ]);
  const fam = Object.entries(s.family_status || {}).filter(([, v]) => v).map(([k]) => `<span class="pill">${esc(optLabel(s, 'family_status', k))}</span>`).join('') || '<span class="muted">None</span>';
  const cov = (s.coverage || []).map((c) => `<span class="pill">${esc(optLabel(s, 'coverage', c))}</span>`).join('') || '<span class="muted">None</span>';
  const custom = customFields(s);
  const customHtml = custom.length
    ? `<div class="section-title">Additional fields</div><dl class="kv">${custom.map((c) => `<dt>${esc(c.label)}</dt><dd>${esc(c.value || '—')}</dd>`).join('')}</dl>`
    : '';
  const stageOpts = ['new', 'assigned', 'signed_up', 'declined', 'left_company']
    .map((k) => `<option value="${k}" ${k === s.status ? 'selected' : ''}>${stageLabel(k)}</option>`).join('');
  const actions = `<label style="align-self:center;font-weight:600;font-size:13px">Stage</label>
       <select id="stageSel" style="max-width:220px">${stageOpts}</select>
       <span class="grow"></span>
       <button class="btn secondary sm" id="pdfBtn">${icon('download')} Download PDF</button>`;
  const memberOpts = `<option value="">Unassigned</option>` + members.map((m) => `<option value="${m.user_id}" ${m.user_id === s.assigned_to ? 'selected' : ''}>${esc(m.email)}</option>`).join('');

  const scrim = openScrim(root, `
    <div class="dh"><h3>${esc(s.employee_name)} <span class="badge ${badge(s.status)}" style="margin-left:6px">${esc(stageLabel(s.status))}</span></h3><button class="close-x">×</button></div>
    <div class="db">
      <dl class="kv">
        <dt>Company</dt><dd>${esc(s.company_name)}</dd>
        <dt>Email</dt><dd>${esc(s.email || '—')}</dd>
        <dt>Phone</dt><dd>${esc(s.phone || '—')}</dd>
        <dt>Hire date</dt><dd>${esc(s.hire_date || '—')}</dd>
        <dt>Submitted</dt><dd>${fmtDateTime(s.submitted_at)}</dd>
      </dl>
      <div class="section-title">Assigned to</div>
      <select id="assignSel">${memberOpts}</select>
      <div class="section-title">Family status</div><div class="pill-list">${fam}</div>
      <div class="section-title">Coverage areas of interest</div><div class="pill-list">${cov}</div>
      ${customHtml}
      <div class="row" style="margin-top:24px;gap:10px" id="subActions">${actions}</div>
      <div class="section-title">Notes</div>
      <div id="noteList">${notesHtml(notes)}</div>
      <textarea id="noteBody" rows="2" placeholder="Add an internal note…" style="margin-top:8px"></textarea>
      <button class="btn sm" id="noteAdd" style="margin-top:8px">Add note</button>
    </div>`);

  (scrim.querySelector('#stageSel') as HTMLSelectElement).onchange = async (e) => {
    const status = (e.target as HTMLSelectElement).value;
    try {
      await api.post(`/api/submissions/${s.id}/status`, { status });
      const item = subState?.all.find((x) => x.id === s.id); if (item) item.status = status;
      s.status = status;
      const badgeEl = scrim.querySelector('.dh .badge') as HTMLElement | null;
      if (badgeEl) { badgeEl.className = `badge ${badge(status)}`; badgeEl.textContent = stageLabel(status); }
      toast(`Stage: ${stageLabel(status)}`);
      renderSubTable(root);
    } catch (err) { toast(err instanceof ApiError ? err.message : 'Failed', 'err'); }
  };

  (scrim.querySelector('#assignSel') as HTMLSelectElement).onchange = async (e) => {
    const userId = (e.target as HTMLSelectElement).value || null;
    try {
      const r = await api.post<{ assignee_email: string | null }>(`/api/submissions/${s.id}/assign`, { user_id: userId });
      const item = subState.all.find((x) => x.id === s.id);
      if (item) { item.assigned_to = userId; item.assignee_email = r.assignee_email; }
      s.assigned_to = userId; s.assignee_email = r.assignee_email;
      toast(userId ? 'Assigned' : 'Unassigned');
      renderSubTable(root);
    } catch (err) { toast(err instanceof ApiError ? err.message : 'Failed', 'err'); }
  };

  (scrim.querySelector('#pdfBtn') as HTMLButtonElement).onclick = async (e) => {
    const btn = e.currentTarget as HTMLButtonElement;
    // Rendering happens server-side and takes a moment; without this the agent
    // has no feedback and clicks again, producing two downloads and two audit
    // rows for one intention.
    btn.disabled = true;
    const label = btn.innerHTML;
    btn.textContent = 'Preparing…';
    try {
      await api.download(`/api/submissions/${s.id}/export.pdf`, `${s.employee_name || 'submission'}.pdf`);
      toast('PDF downloaded');
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not build the PDF', 'err');
    } finally {
      btn.disabled = false;
      btn.innerHTML = label;
    }
  };

  (scrim.querySelector('#noteAdd') as HTMLButtonElement).onclick = async () => {
    const ta = scrim.querySelector('#noteBody') as HTMLTextAreaElement;
    const body = ta.value.trim(); if (!body) return;
    try {
      const r = await api.post<{ note: Note }>(`/api/submissions/${s.id}/notes`, { body });
      notes.push(r.note);
      (scrim.querySelector('#noteList') as HTMLElement).innerHTML = notesHtml(notes);
      ta.value = '';
      toast('Note added');
    } catch (e) { toast(e instanceof ApiError ? e.message : 'Failed', 'err'); }
  };
}

function notesHtml(notes: Note[]): string {
  if (!notes.length) return '<div class="muted" style="font-size:13px">No notes yet.</div>';
  return notes.map((n) => `<div class="note-item"><div class="muted" style="font-size:12px">${esc(n.author_email || 'system')} · ${fmtDateTime(n.created_at)}</div><div>${esc(n.body)}</div></div>`).join('');
}

/* ============================ Branding ============================ */
function brandingBody(agency: Agency): string {
  const b = agency.branding || {};
  return `<div class="grid-2" style="gap:24px;align-items:start">
    <div class="card"><div class="card-head"><h2>White-label settings</h2></div><div class="card-body">
      <div class="field"><label>Agency name</label><input type="text" value="${esc(agency.name)}" disabled /><span class="hint">Set by the platform admin.</span></div>
      <div class="field">
        <label>Logo</label>
        <div class="logo-upload">
          <div class="logo-preview" id="b_logoPreview">${b.logo_url ? `<img src="${esc(b.logo_url)}" alt="logo" />` : '<span class="muted">No logo</span>'}</div>
          <div style="flex:1">
            <input type="file" id="b_logoFile" accept="image/png,image/jpeg,image/webp,image/gif" />
            <div class="row" style="gap:8px;margin-top:8px">
              <button class="btn sm" id="b_logoUpload">Upload</button>
              <button class="btn ghost sm" id="b_logoRemove" ${b.logo_url ? '' : 'style="display:none"'}>Remove</button>
            </div>
            <span class="hint">PNG, JPG, WebP or GIF · up to 1MB.</span>
          </div>
        </div>
      </div>
      <div class="grid-2">
        <div class="field"><label>Primary color</label><input type="color" id="b_primary" value="${esc(b.primary_color || '#1b2a5b')}" style="height:42px;padding:4px" /></div>
        <div class="field"><label>Accent color</label><input type="color" id="b_accent" value="${esc(b.accent_color || '#2e7d32')}" style="height:42px;padding:4px" /></div>
      </div>
      <div class="field"><label>Support email</label><input type="email" id="b_email" value="${esc(b.support_email || '')}" /></div>
      <div class="field"><label>Support phone</label><input type="tel" id="b_phone" value="${esc(b.support_phone || '')}" /></div>
      <div class="field"><label>Footer text</label><input type="text" id="b_footer" value="${esc(b.footer_text || '')}" /></div>
      <button class="btn" id="saveBranding">Save branding</button>
    </div></div>
    <div class="card"><div class="card-head"><h2>Live preview</h2></div><div class="card-body">
      <div class="card" style="overflow:hidden;box-shadow:none">
        <div style="background:var(--brand-primary);color:#fff;padding:18px 20px">
          <div class="logo"><span class="mark" id="bp_mark" style="background:var(--brand-accent)">${esc(initials(agency.name))}</span>
          <span class="name" style="color:#fff">${esc(agency.name)}</span></div>
        </div>
        <div class="card-body"><p style="margin:0 0 16px">Employees see this branding on the form and in emails.</p>
        <button class="btn">Submit form</button></div>
      </div>
    </div></div>
  </div>`;
}

function wireBranding(root: HTMLElement, _agency: Agency) {
  const primary = byId(root, 'b_primary') as HTMLInputElement;
  const accent = byId(root, 'b_accent') as HTMLInputElement;
  const live = () => {
    document.documentElement.style.setProperty('--brand-primary', primary.value);
    document.documentElement.style.setProperty('--brand-accent', accent.value);
    document.documentElement.style.setProperty('--brand-primary-soft', `color-mix(in srgb, ${primary.value} 12%, white)`);
  };
  primary.oninput = live; accent.oninput = live;

  const fileInput = root.querySelector('#b_logoFile') as HTMLInputElement;
  (root.querySelector('#b_logoUpload') as HTMLButtonElement).onclick = async () => {
    const file = fileInput.files?.[0];
    if (!file) { toast('Choose an image first', 'err'); return; }
    if (file.size > 1_000_000) { toast('Image too large (max 1MB)', 'err'); return; }
    try {
      const res = await fetch('/api/agency/logo', { method: 'POST', headers: { 'Content-Type': file.type }, body: file, credentials: 'same-origin' });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.error || 'Upload failed'); }
      toast('Logo uploaded');
      navigate('/app/branding');
    } catch (e) { toast(e instanceof Error ? e.message : 'Upload failed', 'err'); }
  };
  const rm = root.querySelector('#b_logoRemove') as HTMLButtonElement | null;
  if (rm) rm.onclick = async () => {
    try { await api.del('/api/agency/logo'); toast('Logo removed'); navigate('/app/branding'); }
    catch (e) { toast(e instanceof ApiError ? e.message : 'Remove failed', 'err'); }
  };

  byId(root, 'saveBranding').onclick = async () => {
    const branding: Branding = {
      primary_color: primary.value, accent_color: accent.value,
      support_email: inp(root, 'b_email'), support_phone: inp(root, 'b_phone'), footer_text: inp(root, 'b_footer'),
    };
    try { await api.patch('/api/agency/branding', branding); toast('Branding saved'); }
    catch (e) { toast(e instanceof ApiError ? e.message : 'Save failed', 'err'); }
  };
}

/* ============================ helpers ============================ */
function card(title: string, inner: string, flushHead = false): string {
  const head = title || !flushHead ? `<div class="card-head"><h2>${esc(title)}</h2></div>` : '';
  return `<div class="card">${title ? head : ''}<div class="card-body flush">${inner}</div></div>`;
}
function emptyState(iconName: string, title: string, sub: string, actionId: string, actionLabel: string): string {
  return `<div class="card"><div class="card-body"><div class="empty"><span class="emoji">${icon(iconName)}</span>
    <div style="font-weight:600;color:var(--text);font-size:16px">${esc(title)}</div>
    <div>${esc(sub)}</div><button class="btn" id="${actionId}">${icon('plus')} ${esc(actionLabel)}</button></div></div></div>`;
}
function badge(status: string): string {
  switch (status) {
    case 'new': return 'blue';
    case 'assigned': return 'amber';
    case 'signed_up': return 'green';
    default: return 'gray'; // declined, left_company
  }
}
/** Human label for a stage value, e.g. 'signed_up' -> 'Signed up', 'all' -> 'All'. */
function stageLabel(status: string): string { return cap(status.replace(/_/g, ' ')); }
function countBy<T>(arr: T[], key: (t: T) => string): Record<string, number> {
  const m: Record<string, number> = {};
  for (const x of arr) { const k = key(x); if (k) m[k] = (m[k] || 0) + 1; }
  return m;
}
function cap(s: string): string { return s.charAt(0).toUpperCase() + s.slice(1); }
function byId(root: HTMLElement, id: string): HTMLElement { return root.querySelector('#' + id) as HTMLElement; }
function inp(root: ParentNode, id: string): string { const el = root.querySelector('#' + id) as HTMLInputElement | null; return el ? el.value.trim() : ''; }
function initials(name: string): string { const p = name.trim().split(/\s+/); return ((p[0]?.[0] || '') + (p[1]?.[0] || '')).toUpperCase() || '?'; }
function fmtDate(iso: string): string { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
function fmtDateTime(iso: string): string { return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }

function openScrim(root: HTMLElement, drawerInner: string): HTMLElement {
  const scrim = document.createElement('div');
  scrim.className = 'scrim';
  scrim.innerHTML = `<div class="drawer">${drawerInner}</div>`;
  const close = () => scrim.remove();
  scrim.onclick = (e) => { if (e.target === scrim) close(); };
  scrim.querySelector('.close-x')?.addEventListener('click', close);
  root.appendChild(scrim);
  return scrim;
}

/** Returns the scrim so callers can wire extra controls inside the drawer. */
function drawer(root: HTMLElement, title: string, bodyHtml: string, onSubmit: (scrim: HTMLElement) => Promise<string | void>): HTMLElement {
  const scrim = openScrim(root, `
    <div class="dh"><h3>${esc(title)}</h3><button class="close-x">×</button></div>
    <div class="db">${bodyHtml}
      <div id="drawerErr" class="note" style="display:none;margin-top:12px;background:#fdecea;border-color:#f5c6c2;color:#a3261c"></div>
    </div>`);
  const submitBtn = scrim.querySelector('[data-submit]') as HTMLButtonElement | null;
  if (submitBtn) submitBtn.onclick = async () => {
    const errBox = scrim.querySelector('#drawerErr') as HTMLElement;
    submitBtn.disabled = true; const label = submitBtn.textContent; submitBtn.textContent = 'Saving…';
    try {
      const err = await onSubmit(scrim);
      if (err) { errBox.textContent = err; errBox.style.display = 'block'; submitBtn.disabled = false; submitBtn.textContent = label; return; }
      scrim.remove();
    } catch (e) {
      errBox.textContent = e instanceof ApiError ? e.message : 'Something went wrong';
      errBox.style.display = 'block'; submitBtn.disabled = false; submitBtn.textContent = label;
    }
  };
  return scrim;
}
